import React, { useState, useEffect } from 'react';
import { cn } from '../../utils';
import { Button } from '../ui/button';
import { Input, Select } from '../ui/form';
import { useDivisionWorkflows } from '../../hooks/use-division-workflows';
import { useCreateOrderAttribute } from '../../hooks/use-order-attributes';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import type { DiscoveredField } from '../../types';

interface CreateOrderAttributeModalProps {
  isOpen: boolean;
  field: DiscoveredField;
  divisionId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreateOrderAttributeModal({
  isOpen,
  field,
  divisionId,
  onClose,
  onSuccess,
}: CreateOrderAttributeModalProps) {
  const [workflowId, setWorkflowId] = useState('');
  const [label, setLabel] = useState(field.fieldName || '');
  const [fieldType, setFieldType] = useState<'string' | 'number' | 'boolean' | 'date'>('string');
  const [isRequired, setIsRequired] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const [helpText, setHelpText] = useState('');

  const { data: workflowsData, isLoading: workflowsLoading } = useDivisionWorkflows(
    divisionId,
    { active: true },
    !!divisionId
  );
  const createMutation = useCreateOrderAttribute(workflowId);

  const workflows = workflowsData?.items || [];

  // Auto-detect field type from current value
  useEffect(() => {
    if (field.currentValue) {
      const value = field.currentValue;
      if (!isNaN(Number(value))) {
        setFieldType('number');
      } else if (value === 'true' || value === 'false') {
        setFieldType('boolean');
      } else if (!isNaN(Date.parse(value))) {
        // Check if it looks like a date
        const datePattern = /^\d{4}-\d{2}-\d{2}|^\d{2}\/\d{2}\/\d{4}/;
        if (datePattern.test(value)) {
          setFieldType('date');
        }
      }
    }
  }, [field.currentValue]);

  const handleCreate = async () => {
    if (!workflowId || !label) return;

    try {
      await createMutation.mutateAsync({
        workflowId,
        label,
        jsonPath: field.filterBasedJsonPath || field.jsonPath,
        fieldType,
        isRequired,
        isEditable,
        description: helpText || undefined,
        minStep: 1,
        displayOrder: 0,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to create order attribute:', err);
    }
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-order-attribute-modal-title"
        tabIndex={-1}
        className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-plus-circle text-blue-600 dark:text-blue-400" />
            <h3 id="create-order-attribute-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Create Order Attribute from Discovered Field
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Field Info */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              <i className="fa-solid fa-info-circle mr-2" />
              Discovered Field Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Field Name:</span>
                <div className="text-blue-900 font-mono mt-1">{field.fieldName}</div>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Category:</span>
                <div className="text-blue-900 mt-1">{field.category || 'Unknown'}</div>
              </div>
              <div className="col-span-2">
                <span className="text-blue-700 font-medium">JSON Path:</span>
                <code className="block text-blue-900 font-mono text-xs mt-1 bg-white dark:bg-secondary-800 rounded px-2 py-1">
                  {field.filterBasedJsonPath || field.jsonPath}
                </code>
              </div>
              {field.currentValue && (
                <div className="col-span-2">
                  <span className="text-blue-700 font-medium">Current Value:</span>
                  <div className="text-blue-900 mt-1 font-mono text-sm">{field.currentValue}</div>
                </div>
              )}
            </div>
          </div>

          {/* Workflow Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Target Workflow <span className="text-red-500">*</span>
            </label>
            <Select
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              disabled={workflowsLoading}
            >
              <option value="">Select a workflow...</option>
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </Select>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              The order attribute will be associated with this workflow
            </p>
          </div>

          {/* Field Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Display Label <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Supplier Name"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Human-readable label shown to users
            </p>
          </div>

          {/* Field Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Field Type <span className="text-red-500">*</span>
            </label>
            <Select
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value as any)}
            >
              <option value="string">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean (Yes/No)</option>
              <option value="date">Date</option>
            </Select>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Data type for validation and formatting
            </p>
          </div>

          {/* Help Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Help Text (Optional)
            </label>
            <Input
              type="text"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              placeholder="e.g., Enter the supplier's full legal name"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              Guidance text shown to users when editing this field
            </p>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 border-t border-gray-200 dark:border-secondary-700 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                className="mt-1"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Required Field</span>
                <p className="text-xs text-gray-500 dark:text-secondary-400">Users must provide a value for this field</p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isEditable}
                onChange={(e) => setIsEditable(e.target.checked)}
                className="mt-1"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Editable by Users</span>
                <p className="text-xs text-gray-500 dark:text-secondary-400">
                  If unchecked, this field will be display-only (shown with an eye icon)
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!workflowId || !label || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="fa-solid fa-plus mr-2" />
                Create Order Attribute
              </>
            )}
          </Button>
        </div>

        {/* Error Display */}
        {createMutation.isError && (
          <div className="mx-4 mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <i className="fa-solid fa-exclamation-circle" />
              <span className="text-sm">
                Failed to create order attribute. Please try again.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
